import { useState, useMemo } from 'react';
import {
  AlertTriangle,
  AlertCircle,
  ShieldAlert,
  ExternalLink,
  ChevronDown,
  ChevronRight,
  Copy,
  Check,
  FileText,
  Filter,
  CheckCircle,
  Globe,
  Link2,
} from 'lucide-react';
import type { ToxicLink, ToxicityFlag, PageResult } from '../../types';
import { UnverifiedLinksSection } from './UnverifiedLinksSection';

interface ToxicLinksTabProps {
  pageResults: PageResult[];
  loading?: boolean;
  reviewedLinks?: string[];
  onToggleLinkReviewed?: (targetUrls: string[], reviewed: boolean) => void;
  /**
   * Whether this scan also ran the broken-link check. When it did, both checks probe
   * the same URLs and the Links tab already lists everything unverified — repeating
   * it here is the same screen twice. When it did not, this tab is the only place
   * those links appear, so the section stays.
   */
  brokenLinkCheckRan?: boolean;
}

type RiskLevel = 'high' | 'medium' | 'low';
type GroupBy = 'risk' | 'flag' | 'source' | 'none';

// Flag configuration with descriptions and colors
const FLAG_CONFIG: Record<ToxicityFlag, { label: string; description: string; color: string; bgColor: string }> = {
  SUSPICIOUS_TLD: {
    label: 'Suspicious TLD',
    description: 'Domain uses a TLD commonly associated with spam or malicious sites',
    color: 'text-yellow-700',
    bgColor: 'bg-yellow-100',
  },
  DEAD_LINK: {
    label: 'Dead Link',
    description: 'Link returns an error status or is unreachable',
    color: 'text-red-700',
    bgColor: 'bg-red-100',
  },
  REDIRECT_CHAIN: {
    label: 'Redirect Chain',
    description: 'Link redirects through multiple URLs before reaching final destination',
    color: 'text-orange-700',
    bgColor: 'bg-orange-100',
  },
  SUSPICIOUS_PATTERN: {
    label: 'Suspicious Pattern',
    description: 'URL contains patterns commonly associated with spam or malicious content',
    color: 'text-red-700',
    bgColor: 'bg-red-100',
  },
  PARKED_DOMAIN: {
    label: 'Parked Domain',
    description: 'Domain appears to be parked or inactive',
    color: 'text-orange-700',
    bgColor: 'bg-orange-100',
  },
  ADULT_CONTENT: {
    label: 'Adult Content',
    description: 'Domain may contain adult content',
    color: 'text-purple-700',
    bgColor: 'bg-purple-100',
  },
  GAMBLING: {
    label: 'Gambling',
    description: 'Domain appears to be related to gambling',
    color: 'text-blue-700',
    bgColor: 'bg-blue-100',
  },
  PHARMACY: {
    label: 'Pharmacy',
    description: 'Domain may be associated with unregulated pharmaceutical sales',
    color: 'text-green-700',
    bgColor: 'bg-green-100',
  },
};

function getRiskLevel(score: number): RiskLevel {
  if (score >= 70) return 'high';
  if (score >= 30) return 'medium';
  return 'low';
}

function getRiskConfig(level: RiskLevel) {
  switch (level) {
    case 'high':
      return { label: 'High Risk', color: 'text-red-700', bgColor: 'bg-red-100', borderColor: 'border-red-200' };
    case 'medium':
      return { label: 'Medium Risk', color: 'text-orange-700', bgColor: 'bg-orange-100', borderColor: 'border-orange-200' };
    case 'low':
      return { label: 'Low Risk', color: 'text-yellow-700', bgColor: 'bg-yellow-100', borderColor: 'border-yellow-200' };
  }
}

function truncateUrl(url: string, maxLength = 60): string {
  if (url.length <= maxLength) return url;
  try {
    const urlObj = new URL(url);
    const path = urlObj.pathname + urlObj.search;
    if (path.length <= maxLength) return path;
    return '...' + path.slice(-maxLength);
  } catch {
    return '...' + url.slice(-maxLength);
  }
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <button
      onClick={handleCopy}
      className="p-1 hover:bg-gray-200 rounded"
      aria-label={copied ? 'Copied!' : 'Copy URL'}
    >
      {copied ? (
        <Check size={14} className="text-status-success" />
      ) : (
        <Copy size={14} className="text-gray-400" />
      )}
    </button>
  );
}

interface ToxicLinkCardProps {
  link: ToxicLink;
  showSource?: boolean;
}

function ToxicLinkCard({ link, showSource = true }: ToxicLinkCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const riskLevel = getRiskLevel(link.toxicityScore);
  const riskConfig = getRiskConfig(riskLevel);

  return (
    <div className={`border ${riskConfig.borderColor} rounded-lg overflow-hidden bg-white`}>
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-start gap-4 p-4 hover:bg-gray-light/30 transition-colors text-left"
        aria-expanded={isExpanded}
      >
        <div className="flex-1 min-w-0">
          {/* Risk Level and Score */}
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <span className={`px-2 py-0.5 rounded text-caption font-medium ${riskConfig.bgColor} ${riskConfig.color}`}>
              {riskConfig.label} ({link.toxicityScore})
            </span>
            {link.statusCode && link.statusCode >= 400 && (
              <span className="px-2 py-0.5 rounded text-caption bg-red-100 text-red-700">
                HTTP {link.statusCode}
              </span>
            )}
          </div>

          {/* Target URL */}
          <div className="flex items-center gap-2">
            <ShieldAlert size={16} className={riskConfig.color.replace('text-', 'text-')} />
            <span className="text-body font-medium text-dark-text truncate" title={link.targetUrl}>
              {truncateUrl(link.targetUrl)}
            </span>
          </div>

          {/* Domain */}
          <p className="text-body-sm text-gray-medium mt-1">
            Domain: <span className="font-medium">{link.targetDomain}</span>
          </p>

          {/* Link Text */}
          {link.linkText && (
            <p className="text-body-sm text-gray-medium mt-1 line-clamp-1">
              Link text: "{link.linkText}"
            </p>
          )}

          {/* Flags */}
          <div className="flex flex-wrap gap-1 mt-2">
            {link.flags.map((flag) => {
              const config = FLAG_CONFIG[flag];
              return (
                <span
                  key={flag}
                  className={`px-2 py-0.5 rounded text-caption font-medium ${config.bgColor} ${config.color}`}
                  title={config.description}
                >
                  {config.label}
                </span>
              );
            })}
          </div>

          {/* Source (if shown) */}
          {showSource && (
            <p className="text-caption text-gray-medium mt-2">
              Found on: {truncateUrl(link.sourceUrl, 50)}
            </p>
          )}
        </div>

        {isExpanded ? (
          <ChevronDown size={20} className="text-gray-medium flex-shrink-0 mt-1" />
        ) : (
          <ChevronRight size={20} className="text-gray-medium flex-shrink-0 mt-1" />
        )}
      </button>

      {isExpanded && (
        <div className="border-t border-gray-light p-4 bg-gray-light/20 space-y-3">
          {/* Full Target URL */}
          <div>
            <span className="text-caption text-gray-medium block mb-1">Target URL</span>
            <div className="flex items-center gap-2 bg-white p-2 rounded border border-gray-200">
              <a
                href={link.targetUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-body-sm text-coral hover:underline truncate flex-1"
              >
                {link.targetUrl}
              </a>
              <ExternalLink size={14} className="text-gray-400 flex-shrink-0" />
              <CopyButton text={link.targetUrl} />
            </div>
          </div>

          {/* Source URL */}
          <div>
            <span className="text-caption text-gray-medium block mb-1">Source Page</span>
            <div className="flex items-center gap-2 bg-white p-2 rounded border border-gray-200">
              <a
                href={link.sourceUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-body-sm text-coral hover:underline truncate flex-1"
              >
                {link.sourceUrl}
              </a>
              <ExternalLink size={14} className="text-gray-400 flex-shrink-0" />
              <CopyButton text={link.sourceUrl} />
            </div>
          </div>

          {/* Link Text */}
          {link.linkText && (
            <div>
              <span className="text-caption text-gray-medium block mb-1">Link Text</span>
              <p className="text-body-sm bg-white p-2 rounded border border-gray-200">
                {link.linkText || <span className="text-gray-400 italic">No text (image or empty link)</span>}
              </p>
            </div>
          )}

          {/* Redirect Chain */}
          {link.redirectChain && link.redirectChain.length > 0 && (
            <div>
              <span className="text-caption text-gray-medium block mb-1">Redirect Chain</span>
              <div className="bg-white p-2 rounded border border-gray-200 space-y-1">
                {link.redirectChain.map((url, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-body-sm">
                    <span className="text-gray-400">{idx + 1}.</span>
                    <a
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-coral hover:underline truncate"
                    >
                      {truncateUrl(url, 50)}
                    </a>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Flag Details */}
          <div>
            <span className="text-caption text-gray-medium block mb-1">Toxicity Flags</span>
            <div className="bg-white p-2 rounded border border-gray-200 space-y-2">
              {link.flags.map((flag) => {
                const config = FLAG_CONFIG[flag];
                return (
                  <div key={flag} className="flex items-start gap-2">
                    <span className={`px-2 py-0.5 rounded text-caption font-medium ${config.bgColor} ${config.color}`}>
                      {config.label}
                    </span>
                    <span className="text-body-sm text-gray-medium">{config.description}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SkeletonCard() {
  return (
    <div className="border border-gray-light rounded-lg p-4 animate-pulse">
      <div className="flex items-center gap-2 mb-2">
        <div className="h-5 w-24 bg-gray-light rounded" />
        <div className="h-5 w-16 bg-gray-light rounded" />
      </div>
      <div className="h-4 w-3/4 bg-gray-light rounded mb-2" />
      <div className="h-3 w-1/2 bg-gray-light rounded" />
    </div>
  );
}

export function ToxicLinksTab({
  pageResults,
  loading = false,
  reviewedLinks,
  onToggleLinkReviewed,
  brokenLinkCheckRan = false,
}: ToxicLinksTabProps) {
  const [groupBy, setGroupBy] = useState<GroupBy>('risk');

  // Collect the links the check reported, then split them. A link whose probe never
  // settled (anti-bot 403, rate limit, 5xx, timeout) carries no toxicity flags and
  // no score, so it stays out of every count below and is listed on its own for a
  // human to open.
  const { allToxicLinks, unverifiedLinks } = useMemo(() => {
    const toxic: ToxicLink[] = [];
    const unverified: ToxicLink[] = [];
    for (const page of pageResults) {
      for (const link of page.toxicLinks ?? []) {
        (link.needsManualReview ? unverified : toxic).push(link);
      }
    }
    // Sort by toxicity score descending
    return {
      allToxicLinks: toxic.sort((a, b) => b.toxicityScore - a.toxicityScore),
      unverifiedLinks: unverified,
    };
  }, [pageResults]);

  // Summary counts
  const riskCounts = useMemo(() => {
    return allToxicLinks.reduce(
      (acc, link) => {
        const level = getRiskLevel(link.toxicityScore);
        acc[level]++;
        acc.total++;
        return acc;
      },
      { high: 0, medium: 0, low: 0, total: 0 }
    );
  }, [allToxicLinks]);

  // Count by flag type
  const flagCounts = useMemo(() => {
    const counts: Partial<Record<ToxicityFlag, number>> = {};
    for (const link of allToxicLinks) {
      for (const flag of link.flags) {
        counts[flag] = (counts[flag] || 0) + 1;
      }
    }
    return counts;
  }, [allToxicLinks]);

  // Group by risk level
  const linksByRisk = useMemo(() => {
    const groups: Record<RiskLevel, ToxicLink[]> = { high: [], medium: [], low: [] };
    for (const link of allToxicLinks) {
      const level = getRiskLevel(link.toxicityScore);
      groups[level].push(link);
    }
    return groups;
  }, [allToxicLinks]);

  // Group by flag
  const linksByFlag = useMemo(() => {
    const groups = new Map<ToxicityFlag, ToxicLink[]>();
    for (const link of allToxicLinks) {
      for (const flag of link.flags) {
        const existing = groups.get(flag) || [];
        groups.set(flag, [...existing, link]);
      }
    }
    return groups;
  }, [allToxicLinks]);

  // Group by source page
  const linksBySource = useMemo(() => {
    const groups = new Map<string, ToxicLink[]>();
    for (const link of allToxicLinks) {
      const existing = groups.get(link.sourceUrl) || [];
      groups.set(link.sourceUrl, [...existing, link]);
    }
    return new Map([...groups.entries()].sort((a, b) => b[1].length - a[1].length));
  }, [allToxicLinks]);

  if (loading) {
    return (
      <div className="space-y-8">
        <div className="card">
          <h3 className="text-heading-3 font-semibold mb-6 flex items-center gap-2">
            <ShieldAlert className="text-coral" size={24} />
            Toxic Outbound Links
          </h3>
          <div className="animate-pulse">
            <div className="h-20 bg-gray-light rounded mb-4" />
            <div className="grid grid-cols-3 gap-4">
              <div className="h-16 bg-gray-light rounded" />
              <div className="h-16 bg-gray-light rounded" />
              <div className="h-16 bg-gray-light rounded" />
            </div>
          </div>
        </div>
        <div>
          <h3 className="text-heading-3 font-semibold mb-4">All Toxic Links</h3>
          <div className="space-y-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Summary */}
      <div className="card">
        <h3 className="text-heading-3 font-semibold mb-6 flex items-center gap-2">
          <ShieldAlert className="text-coral" size={24} />
          Toxic Outbound Links
        </h3>

        {allToxicLinks.length > 0 ? (
          <>
            {/* Total count */}
            <div className="flex items-center gap-4 mb-6">
              <div className="flex items-center gap-2">
                <AlertTriangle size={24} className="text-status-warning" />
                <span className="text-heading-1 font-bold text-status-warning">{riskCounts.total}</span>
                <span className="text-body text-gray-medium">potentially toxic outbound links found</span>
              </div>
            </div>

            {/* Risk Breakdown */}
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="p-3 bg-red-50 rounded-lg border border-red-100">
                <div className="flex items-center gap-2 mb-1">
                  <AlertCircle size={18} className="text-red-600" />
                  <span className="text-heading-3 font-bold text-red-800">{riskCounts.high}</span>
                </div>
                <div className="text-body-sm text-gray-medium">High Risk (70+)</div>
              </div>
              <div className="p-3 bg-orange-50 rounded-lg border border-orange-100">
                <div className="flex items-center gap-2 mb-1">
                  <AlertTriangle size={18} className="text-orange-600" />
                  <span className="text-heading-3 font-bold text-orange-800">{riskCounts.medium}</span>
                </div>
                <div className="text-body-sm text-gray-medium">Medium Risk (30-69)</div>
              </div>
              <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-100">
                <div className="flex items-center gap-2 mb-1">
                  <AlertTriangle size={18} className="text-yellow-600" />
                  <span className="text-heading-3 font-bold text-yellow-800">{riskCounts.low}</span>
                </div>
                <div className="text-body-sm text-gray-medium">Low Risk (1-29)</div>
              </div>
            </div>

            {/* Flag breakdown */}
            <div className="border-t border-gray-light pt-4">
              <h4 className="text-body font-semibold text-gray-medium mb-3">Issues Detected</h4>
              <div className="flex flex-wrap gap-2">
                {(Object.entries(flagCounts) as [ToxicityFlag, number][])
                  .sort((a, b) => b[1] - a[1])
                  .map(([flag, count]) => {
                    const config = FLAG_CONFIG[flag];
                    return (
                      <span
                        key={flag}
                        className={`inline-flex items-center gap-1 px-2 py-1 rounded text-caption font-medium ${config.bgColor} ${config.color}`}
                      >
                        {config.label}
                        <span className="bg-white/50 px-1.5 rounded">{count}</span>
                      </span>
                    );
                  })}
              </div>
            </div>

            {/* Info box */}
            <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-100 text-body-sm text-blue-800">
              <div className="flex items-start gap-2">
                <Globe size={16} className="flex-shrink-0 mt-0.5" />
                <p>
                  Toxic outbound links are external links from your website that point to suspicious, spammy, or potentially harmful domains.
                  These can negatively impact your SEO and may harm your visitors if they click through.
                </p>
              </div>
            </div>
          </>
        ) : (
          <div className="text-center py-8">
            <CheckCircle size={48} className="mx-auto text-status-success opacity-50 mb-4" />
            <h4 className="text-heading-3 font-semibold text-dark-text mb-2">
              No Toxic Links Found
            </h4>
            <p className="text-body text-gray-medium">
              No potentially harmful outbound links were detected on the scanned pages.
            </p>
          </div>
        )}
      </div>

      {/* Links we could not verify - listed, not scored */}
      {!brokenLinkCheckRan && (
        <UnverifiedLinksSection
          links={unverifiedLinks}
          subject="outbound links"
          reviewedLinks={reviewedLinks}
          onToggleReviewed={onToggleLinkReviewed}
        />
      )}

      {/* Links List */}
      {allToxicLinks.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-heading-3 font-semibold flex items-center gap-2">
              <Link2 className="text-coral" size={24} />
              All Toxic Links
            </h3>

            {/* Group By Selector */}
            <div className="flex items-center gap-2">
              <Filter size={14} className="text-gray-medium" />
              <span className="text-body-sm text-gray-medium">Group by:</span>
              <select
                value={groupBy}
                onChange={(e) => setGroupBy(e.target.value as GroupBy)}
                className="appearance-none bg-white border border-gray-200 rounded-lg px-3 py-1.5 pr-8 text-body-sm focus:outline-none focus:ring-2 focus:ring-coral/30"
              >
                <option value="risk">Risk Level</option>
                <option value="flag">Issue Type</option>
                <option value="source">Source Page</option>
                <option value="none">No Grouping</option>
              </select>
            </div>
          </div>

          {groupBy === 'risk' && (
            <div className="space-y-6">
              {(['high', 'medium', 'low'] as RiskLevel[]).map((level) => {
                const links = linksByRisk[level];
                if (links.length === 0) return null;
                const config = getRiskConfig(level);
                return (
                  <div key={level}>
                    <h4 className="text-body font-semibold text-gray-medium mb-3 flex items-center gap-2">
                      <span className={`px-2 py-0.5 rounded ${config.bgColor} ${config.color}`}>
                        {config.label}
                      </span>
                      <span className="text-body-sm font-normal">({links.length} links)</span>
                    </h4>
                    <div className="space-y-3">
                      {links.map((link, idx) => (
                        <ToxicLinkCard key={`${link.targetUrl}-${idx}`} link={link} />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {groupBy === 'flag' && (
            <div className="space-y-6">
              {Array.from(linksByFlag.entries())
                .sort((a, b) => b[1].length - a[1].length)
                .map(([flag, links]) => {
                  const config = FLAG_CONFIG[flag];
                  return (
                    <div key={flag}>
                      <h4 className="text-body font-semibold text-gray-medium mb-3 flex items-center gap-2">
                        <span className={`px-2 py-0.5 rounded ${config.bgColor} ${config.color}`}>
                          {config.label}
                        </span>
                        <span className="text-body-sm font-normal">({links.length} links)</span>
                      </h4>
                      <div className="space-y-3">
                        {links.map((link, idx) => (
                          <ToxicLinkCard key={`${link.targetUrl}-${idx}`} link={link} />
                        ))}
                      </div>
                    </div>
                  );
                })}
            </div>
          )}

          {groupBy === 'source' && (
            <div className="space-y-6">
              {Array.from(linksBySource.entries()).map(([sourceUrl, links]) => (
                <div key={sourceUrl}>
                  <h4 className="text-body font-semibold text-gray-medium mb-3 flex items-center gap-2">
                    <FileText size={16} />
                    <a
                      href={sourceUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-coral hover:underline"
                    >
                      {truncateUrl(sourceUrl, 50)}
                    </a>
                    <span className="text-body-sm font-normal">({links.length} toxic links)</span>
                  </h4>
                  <div className="space-y-3">
                    {links.map((link, idx) => (
                      <ToxicLinkCard key={`${link.targetUrl}-${idx}`} link={link} showSource={false} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {groupBy === 'none' && (
            <div className="space-y-3">
              {allToxicLinks.map((link, idx) => (
                <ToxicLinkCard key={`${link.targetUrl}-${idx}`} link={link} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
